import { useCallback, useEffect, useRef, useState } from "react";
import { MediaItem, useMediaPreview } from "components/MediaPreview";
import { isGoogleDriveUrl } from "util/googleDriveHelper";
import { isTiff, tiffToPngUrl } from "util/tiffPreview";

type UploadedImage = { url: string; name?: string };
type PreviewImage = File | UploadedImage;

const FALLBACK_IMAGE_MIME = "image/jpeg";
const CONVERTED_IMAGE_MIME = "image/png";

const needsTiffConversion = (image: PreviewImage): boolean => {
  if (image instanceof File) return isTiff(image.name, image.type);
  return Boolean(image?.url) && !isGoogleDriveUrl(image.url) && isTiff(image.url);
};

const loadImageBlob = async (image: PreviewImage): Promise<Blob> => {
  if (image instanceof File) return image;
  const response = await fetch(image.url);
  if (!response.ok) throw new Error("Image download failed");
  return response.blob();
};

export function useInventoryImagePreview(images: PreviewImage[]) {
  const { previewProps, open, close, isOpen } = useMediaPreview();
  const savedOverflowRef = useRef<string | null>(null);
  const objectUrlsRef = useRef<string[]>([]);
  const convertedUrlsRef = useRef(new Map<PreviewImage, string>());
  const requestedRef = useRef(new Set<PreviewImage>());
  const mountedRef = useRef(true);
  const [, setConvertedCount] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    const convertedUrls = convertedUrlsRef.current;
    const requested = requestedRef.current;
    return () => {
      mountedRef.current = false;
      convertedUrls.forEach((url) => URL.revokeObjectURL(url));
      convertedUrls.clear();
      requested.clear();
    };
  }, []);

  useEffect(() => {
    const currentImages = new Set(images);
    const convertedUrls = convertedUrlsRef.current;
    const requested = requestedRef.current;

    convertedUrls.forEach((url, image) => {
      if (currentImages.has(image)) return;
      URL.revokeObjectURL(url);
      convertedUrls.delete(image);
    });
    requested.forEach((image) => {
      if (!currentImages.has(image)) requested.delete(image);
    });

    images
      .filter((image) => needsTiffConversion(image) && !requested.has(image))
      .forEach((image) => {
        requested.add(image);
        loadImageBlob(image)
          .then(tiffToPngUrl)
          .then((url) => {
            if (!mountedRef.current || !requested.has(image) || convertedUrls.has(image)) {
              URL.revokeObjectURL(url);
              return;
            }
            convertedUrls.set(image, url);
            setConvertedCount((count) => count + 1);
          })
          .catch(() => undefined);
      });
  }, [images]);

  const getDisplayUrl = useCallback((image: PreviewImage): string => {
    const convertedUrl = convertedUrlsRef.current.get(image);
    if (convertedUrl) return convertedUrl;
    if (image instanceof File) return URL.createObjectURL(image);
    return image.url;
  }, []);

  const toMediaItem = useCallback((image: PreviewImage): MediaItem => {
    const convertedUrl = convertedUrlsRef.current.get(image);
    if (image instanceof File) {
      if (convertedUrl) {
        return { url: convertedUrl, name: image.name, mimeType: CONVERTED_IMAGE_MIME };
      }
      const url = URL.createObjectURL(image);
      objectUrlsRef.current.push(url);
      return { url, name: image.name, mimeType: image.type || FALLBACK_IMAGE_MIME };
    }
    return {
      url: convertedUrl || image.url,
      name: image.name || "image",
      mimeType: convertedUrl ? CONVERTED_IMAGE_MIME : FALLBACK_IMAGE_MIME,
    };
  }, []);

  const openAt = useCallback(
    (previewImages: PreviewImage[], index: number) => {
      savedOverflowRef.current = document.body.style.overflow;
      open(previewImages.map(toMediaItem), index);
    },
    [open, toMediaItem],
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen || savedOverflowRef.current === null) return;
    document.body.style.overflow = savedOverflowRef.current;
    savedOverflowRef.current = null;
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];
  }, [isOpen]);

  return { previewProps, openAt, getDisplayUrl };
}
